// ============================================
// Gemini AI Provider — Cloud Function Proxy
//
// All Gemini calls route through geminiProxy Cloud Function.
// API key is NEVER exposed client-side.
//
// K=TOP: mirrors claudeProxy pattern.
// ============================================

import { CF_URLS } from '../../../config/api';

const PROXY_URL = CF_URLS.geminiProxy;

export interface GeminiMessage {
  role: 'user' | 'model';
  parts: Array<{ text: string }>;
}

interface GeminiProxyResponse {
  text: string;
}

/** Fetch with an AbortController timeout */
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Retry helper for transient Cloud Function failures (cold starts, fetch failed) */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 1,
  timeoutMs = 60000,
): Promise<Response> {
  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs);
      if (response.ok || response.status < 500) return response;
      if (attempt <= maxRetries) {
        const errorText = await response.text();
        console.warn(`[Gemini] Attempt ${attempt} got ${response.status}, retrying in 3s... (${errorText.slice(0, 100)})`);
        await new Promise(r => setTimeout(r, 3000));
        continue;
      }
      return response;
    } catch (err) {
      if (attempt <= maxRetries) {
        console.warn(`[Gemini] Attempt ${attempt} failed, retrying in 3s...`, err);
        await new Promise(r => setTimeout(r, 3000));
        continue;
      }
      throw err;
    }
  }
  throw new Error('fetchWithRetry: unreachable');
}

/**
 * Single-turn text generation with Gemini (via Cloud Function proxy)
 */
export async function geminiGenerate(
  model: string,
  systemPrompt: string,
  userMessage: string,
  temperature = 0.7,
  maxTokens = 2048,
): Promise<string> {
  const response = await fetchWithRetry(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mode: 'generate',
      model,
      systemPrompt,
      userMessage,
      temperature,
      maxTokens,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Gemini proxy error (${response.status}): ${errorText}`);
  }

  const data: GeminiProxyResponse = await response.json();
  return data.text;
}

export interface GeminiCallMetadata {
  sessionId?: string;
  callerFunction?: string;
}

/**
 * Multi-turn chat with Gemini (via Cloud Function proxy)
 */
export async function geminiChat(
  model: string,
  systemPrompt: string,
  history: GeminiMessage[],
  userMessage: string,
  temperature = 0.7,
  maxTokens = 2048,
  metadata?: GeminiCallMetadata,
): Promise<string> {
  const response = await fetchWithRetry(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mode: 'chat',
      model,
      systemPrompt,
      history,
      userMessage,
      temperature,
      maxTokens,
      ...(metadata?.callerFunction && { callerFunction: metadata.callerFunction }),
      ...(metadata?.sessionId && { sessionId: metadata.sessionId }),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Gemini proxy error (${response.status}): ${errorText}`);
  }

  const data: GeminiProxyResponse = await response.json();
  return data.text;
}

/**
 * Vision call with Gemini (via Cloud Function proxy)
 *
 * NOTE: Vision with inline image data is not yet supported through the proxy.
 * This function is preserved for API compatibility but will throw if called.
 * When vision support is needed, extend the geminiProxy Cloud Function
 * to accept inlineData parts.
 */
export async function geminiVision(
  _model: string,
  _systemPrompt: string,
  _imageBase64: string,
  _mimeType: string,
  _userMessage: string,
  _temperature = 0.3,
  _maxTokens = 4096,
): Promise<string> {
  throw new Error(
    'geminiVision is not yet supported through the server-side proxy. ' +
    'Use the extractLabPDF Cloud Function for PDF/image analysis instead.',
  );
}
